import { useMemo, useState } from "react";
import { Archive, ArchiveRestore, CalendarDays, Search, AlertTriangle } from "lucide-react";
import { api, fmtErr } from "@/lib/api";
import { useAdminLanguage } from "@/i18n/AdminLanguage";

const boxClass = "rounded-2xl border border-white/10 bg-ash/85 p-5";
const selectClass = "min-w-0 rounded-lg border border-white/15 bg-coal px-3 py-2.5 text-sm text-white outline-none focus:border-flame";
const copy = {
  es: {
    title:"Archivo de fiestas", detail:"Organiza fiestas antiguas sin borrar ventas, QR ni estadísticas. Este filtro solo afecta esta lista.",
    filter:"Mostrar", active:"Activas", past:"Anteriores", archived:"Archivadas", all:"Todas", notArchived:"Sin archivar",
    search:"Buscar por nombre o fecha", empty:"No hay fiestas en esta categoría.",
    archive:"Archivar", restore:"Restaurar", archivedStatus:"Archivada", withdrawn:"Retirada", closed:"Cerrada", activeStatus:"Activa", pastStatus:"Anterior",
    yesArchive:"¿Archivar esta fiesta? No se borrarán ventas, entradas ni estadísticas.",
    yesRestore:"¿Restaurar esta fiesta al listado habitual?", saved:"Cambio guardado en D1.",
    blocked:"Las fiestas activas y futuras no se pueden archivar. Primero cierra la venta de entradas desde Events.",
    refreshNote:"Puedes volver a consultar las archivadas cuando quieras. La gráfica de ingresos y los totales generales mantienen todos los datos.",
  },
  en: {
    title:"Party archive", detail:"Organize older parties without deleting sales, QR codes or statistics. This filter only affects this list.",
    filter:"Show", active:"Active", past:"Past", archived:"Archived", all:"All", notArchived:"Not archived",
    search:"Search name or date", empty:"No parties in this category.",
    archive:"Archive", restore:"Restore", archivedStatus:"Archived", withdrawn:"Withdrawn", closed:"Closed", activeStatus:"Active", pastStatus:"Past",
    yesArchive:"Archive this party? Sales, tickets and statistics will not be deleted.",
    yesRestore:"Restore this party to the regular list?", saved:"Change saved in D1.",
    blocked:"Active future parties cannot be archived. Close ticket sales in Events first.",
    refreshNote:"You can view archived parties any time. The revenue chart and global totals still include historical data.",
  },
  ru: {
    title:"Архив вечеринок", detail:"Упорядочивайте старые вечеринки, сохраняя продажи, QR-коды и статистику. Этот фильтр меняет только данный список.",
    filter:"Показать", active:"Активные", past:"Прошедшие", archived:"В архиве", all:"Все", notArchived:"Не в архиве",
    search:"Поиск по названию или дате", empty:"Нет вечеринок в этой категории.",
    archive:"В архив", restore:"Восстановить", archivedStatus:"В архиве", withdrawn:"Скрыта", closed:"Закрыта", activeStatus:"Активна", pastStatus:"Прошла",
    yesArchive:"Переместить вечеринку в архив? Продажи, билеты и статистика сохранятся.",
    yesRestore:"Вернуть вечеринку в обычный список?", saved:"Изменение сохранено в D1.",
    blocked:"Будущие активные вечеринки нельзя архивировать. Сначала закройте продажи в Events.",
    refreshNote:"Архивные вечеринки доступны в любое время. График доходов и общая статистика сохраняют все данные.",
  },
};
const yekNow = () => new Date().toLocaleString("sv-SE", {timeZone:"Asia/Yekaterinburg", hour12:false}).slice(0,16);
const isPast = e => String(e.event_date || "") <= yekNow();
const isCurrent = e => !Number(e.archived) && !Number(e.hidden) && e.status === "active" && !isPast(e);

export default function AdminKndlaArchive({ events = [], onChanged }) {
  const { lang } = useAdminLanguage();
  const c = copy[lang] || copy.es;
  const [scope, setScope] = useState("past");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const visible = useMemo(() => events.filter(e => {
    const matching = `${e.name || ""} ${e.event_date || ""}`.toLowerCase().includes(search.trim().toLowerCase());
    if (!matching) return false;
    if (scope === "active") return isCurrent(e);
    if (scope === "past") return !Number(e.archived) && isPast(e);
    if (scope === "archived") return Boolean(Number(e.archived));
    if (scope === "notArchived") return !Number(e.archived);
    return true;
  }).sort((a,b)=>String(b.event_date||"").localeCompare(String(a.event_date||"")) || Number(b.id)-Number(a.id)), [events,scope,search]);

  async function changeArchive(e) {
    const archived = Boolean(Number(e.archived));
    if (!archived && !window.confirm(c.yesArchive)) return;
    if (archived && !window.confirm(c.yesRestore)) return;
    setBusy(e.id); setError(""); setNotice("");
    try {
      if (archived) await api.delete(`/kndla/archive/${encodeURIComponent(e.id)}`);
      else await api.put(`/kndla/archive/${encodeURIComponent(e.id)}`);
      await onChanged(e, !archived);
      setNotice(c.saved);
    } catch (err) {
      setError(fmtErr(err, "No se pudo actualizar el archivo"));
    } finally {setBusy(null);}
  }

  return <section className={boxClass} aria-label={c.title}>
    <div className="flex items-center gap-2"><Archive size={18} className="text-flame"/><h3 className="font-syne text-lg font-bold text-white">{c.title}</h3></div>
    <p className="mt-2 text-xs leading-5 text-white/45">{c.detail}</p>
    <div className="mt-4 grid gap-3 sm:grid-cols-2">
      <label><span className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-white/50">{c.filter}</span>
        <select value={scope} onChange={event=>setScope(event.target.value)} className={`${selectClass} w-full`}>
          <option value="active">{c.active}</option><option value="past">{c.past}</option>
          <option value="archived">{c.archived}</option><option value="notArchived">{c.notArchived}</option><option value="all">{c.all}</option>
        </select>
      </label>
      <label><span className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-white/50">{c.search}</span>
        <div className="relative"><Search size={15} className="absolute left-3 top-3 text-white/40" aria-hidden="true" />
          <input value={search} onChange={event=>setSearch(event.target.value)} className={`${selectClass} w-full pl-9`} placeholder={c.search}/>
        </div>
      </label>
    </div>
    {error && <p role="alert" className="mt-3 flex items-center gap-2 text-sm text-red-300"><AlertTriangle size={15}/>{error}</p>}
    {notice && <p role="status" className="mt-3 text-xs text-emerald-300">{notice}</p>}
    <div className="mt-4 max-h-96 overflow-y-auto rounded-xl border border-white/10 divide-y divide-white/10">
      {visible.slice(0,100).map(e=>{
        const archived = Boolean(Number(e.archived));
        const futureActive = e.status === "active" && !isPast(e);
        const disabled = Boolean(busy) || (!archived && futureActive);
        const status = archived ? c.archivedStatus : Number(e.hidden) ? c.withdrawn : futureActive ? c.activeStatus : isPast(e) ? c.pastStatus : c.closed;
        return <div key={e.id} className="flex flex-wrap items-center justify-between gap-3 px-3 py-3">
          <div className="min-w-0 flex-1"><p className="break-words text-sm font-semibold text-white">{e.name}</p>
            <p className="mt-1 flex items-center gap-1.5 text-[11px] text-white/40"><CalendarDays size={12}/>{e.event_date || "—"} · {status}</p></div>
          <button type="button" disabled={disabled} title={!archived && futureActive ? c.blocked : ""}
            onClick={()=>changeArchive(e)} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-white/20 px-3 py-2 text-xs font-semibold text-white hover:border-flame disabled:cursor-not-allowed disabled:opacity-40">
            {archived ? <ArchiveRestore size={14}/> : <Archive size={14}/>} {busy === e.id ? "…" : archived ? c.restore : c.archive}
          </button>
        </div>;
      })}
      {!visible.length && <p className="p-5 text-center text-xs text-white/50">{c.empty}</p>}
      {visible.length > 100 && <p className="p-3 text-center text-xs text-white/50">100 / {visible.length}</p>}
    </div>
    <p className="mt-3 text-[11px] leading-5 text-white/40">{c.refreshNote}</p>
  </section>;
}
